import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { createPortal } from 'react-dom';
import { ArrowTopRightOnSquareIcon, ClockIcon } from '@heroicons/react/24/outline';
import { config } from '../../../config/config';
import RecordHistoryDrawer from '../../../components/RecordHistoryDrawer';
import ServiceFailureTypeHelp from '../../../components/ServiceFailureTypeHelp';
import troubleTicketsService, {
  type TroubleTicket,
  type TroubleTicketCategory,
  type RescheduleTriggerSkipReason,
  type RescheduleDateTarget,
  type ReschedulePrepullTarget,
  type RescheduleEnRouteLeg,
} from '../../../services/troubleTickets.service';
import schedulingService from '../../../services/scheduling.service';
import { DOWNSTREAM_SKIP_REASON_LABELS } from '../../../utils/legFieldGuards';
import { useChangeAll } from '../changeAll/ChangeAllContext';
import ChangeAllReviewModal from '../changeAll/ChangeAllReviewModal';
import {
  bulkBlockedReason, buildBulkTicket, buildBulkSf, rescheduleFanoutPrompt,
  rescheduleFanoutSummary, RESCHEDULE_DELIVERY_LABEL, RESCHEDULE_PREPULL_LABEL,
  type RescheduleDateChoice,
} from '../changeAll/troubleTicketFanout';
import { useCanEditDriverField } from '../useCanEditDriverField';
import repairOrdersService from '../../../services/repairOrders.service';
import { RescheduleHistoryItem } from '../../../types/scheduling';
import { DriverLookup } from '../../../types/repairOrders';
import RepairOrderPingControls from '../../../components/RepairOrderPingControls';
import { useRepairOrderPings } from '../../../hooks/useRepairOrderPings';
import { repairOrderRecnoForTicket } from '../../../utils/repairOrderTicketLink';
import DateTimePicker from '../../../components/DateTimePicker';
import TypeAhead, { TypeAheadOption } from '../../../components/TypeAhead';
import { THD_BILLTOS } from '../../../constants/billtosCustomers';
import {
  SERVICE_FAILURE_TYPE_OPTIONS,
  SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE,
  isControllableReason,
  serviceFailureTypeMissing,
} from '../../../constants/serviceFailureTypes';
import { useToast } from '../../../contexts/ToastContext';
import {
  useTroubleTicketSaveListener,
  type TroubleTicketSaveResult,
} from '../../../contexts/TroubleTicketSaveContext';

export interface TroubleTicketEditModalProps {
  pronumber: number;
  /** Present = edit mode, absent = create mode. */
  ticket?: TroubleTicket;
  /**
   * Create-mode only: seed values merged over the defaults (`status: 'NEW'`,
   * `driverticket: 'N'`). Used by the auto-triggered REJECTED ticket (legacy
   * cms.js leg_movetype) to pre-set `driverticket: 'Y'` + `category: 'REJECTED'`
   * while leaving every field user-editable. Ignored in edit mode.
   */
  initialEdits?: Partial<TroubleTicket>;
  /**
   * Create-mode only: pin the Category to whatever `initialEdits` seeded and stop the
   * user changing it. For a flow that exists to raise ONE specific ticket — the THD
   * `SHIPMENT CANCELLED` offered when a Home Depot-family order is deleted — the
   * category IS the prompt; letting it be edited would turn a declined cancellation
   * notice into an unrelated ticket. Everything else on the form stays editable, and
   * Cancel still means "no ticket".
   */
  lockedCategory?: boolean;
  /** Order's billto (used to gate THD-only categories + sent on create). */
  billTo?: number;
  onClose: () => void;
  /** Called after a successful create or update. */
  onSaved: () => void;
  /**
   * Called after the Reschedule Requested side effect resolves (a skip, or
   * an applied clear once the user answers the "remove the scheduled date?"
   * prompt) so the parent can re-fetch the drawer — cleared driver/dates/
   * actuals and any new reschedule row appear without a page reload.
   */
  onRescheduleApplied?: () => void | Promise<void>;
  /**
   * Called after the embedded service failure (1:1 reschedule) is created or
   * updated, so the pro can refresh its Service Failures count + panel without
   * a full order refetch.
   */
  onServiceFailureSaved?: () => void;
  /**
   * True when the order this ticket belongs to has unsaved edits. Blocks
   * creating a "Reschedule Requested" ticket until they're saved — the
   * server-side reschedule reads the order's saved state, so pending changes
   * would be missed. Only passed when opened from the order edit view.
   */
  hasUnsavedOrderChanges?: boolean;
  /**
   * Presentation: 'modal' (default) renders a centered dialog; 'drawer' renders
   * the same form as a right-side panel (used by the Dispatch Dashboard to keep
   * the prior drawer UX).
   */
  variant?: 'modal' | 'drawer';
  /**
   * Drawer-only: when the ticket is opened by recno (not from a pro), supply a
   * handler so the header can show a clickable PRO that opens the order drawer.
   * Omitted in modal mode — that always opens from the pro itself, so the link
   * would be redundant.
   */
  onOpenPro?: (pronumber: number) => void;
}


/**
 * Categories that present a reason dropdown. Only THD tickets use it — the
 * options come from the thdstatusreason table via
 * /trouble-tickets/reasons-by-category. No other category shows a reason.
 */
const CATEGORIES_WITH_REASONS = [
  'THD -',
];

function categoryHasReasonList(category: string): boolean {
  if (!category) return false;
  return CATEGORIES_WITH_REASONS.some((prefix) => {
    if (category === prefix) return true;
    if (category.startsWith(prefix + ' ')) return true;
    if (prefix.endsWith('-') && category.startsWith(prefix)) return true;
    return false;
  });
}

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'NEW', label: 'New' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const ASSIGNED_TO_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'CSR', label: 'CSR' },
  { value: 'DISPATCHER', label: 'Dispatcher' },
];

/** Friendly labels for the trouble-ticket columns logged to cms.history
 *  (dbtable='troubleticket'). Used by the per-record History sub-drawer. */
const TT_HISTORY_FIELD_LABELS: Record<string, string> = {
  category: 'Category',
  status: 'Status',
  driverticket: 'Driver Ticket',
  preventable: 'Preventable',
  issue: 'Issue',
  assignedto: 'Assigned To',
  responsible: 'Responsible',
  carriercode: 'Carrier Code',
  servicefailure: 'Service Failure',
  resolve: 'Resolution',
  reviewedby: 'Reviewed By',
  revieweddatetime: 'Reviewed Date/Time',
};

/**
 * Toast copy for each reschedule skip reason. Shared by the create-time
 * evaluate (phase 1) and the post-confirmation apply (phase 2), since the
 * gates — and therefore the reasons — are identical on both.
 */
const RESCHEDULE_SKIP_MESSAGES: Record<RescheduleTriggerSkipReason, string> = {
  no_delivery_leg:
    'Trouble ticket saved. No delivery leg found on this pro — no reschedule was applied.',
  no_date:
    'Trouble ticket saved. The delivery appointment date is already blank — no reschedule was applied.',
  unparseable_date:
    'Trouble ticket saved. Delivery appointment date could not be read — no reschedule was applied.',
  no_legs_in_group:
    'Trouble ticket saved. Could not resolve the delivery leg dispatch group — no reschedule was applied.',
  group_has_locked_leg:
    'Trouble ticket saved. A leg on this dispatch is already paid or completed — no reschedule was applied so the leg is not left blanked and locked.',
};

/** Shown when a "Reschedule Requested" ticket can't be created because the
 * order has unsaved edits. The reschedule side effect reads the order's SAVED
 * state, so pending changes would be missed (and the wrong dates cleared). */
const RESCHEDULE_UNSAVED_ORDER_MESSAGE =
  'This order has unsaved changes. Save or discard them before creating a ' +
  '"Reschedule Requested" ticket — the reschedule is generated from the ' +
  "order's saved details, so pending edits would be missed.";

/** Editable fields of the embedded service failure (servicefailuredate as
 * 'YYYY-MM-DD'). */
type SfFields = {
  servicefailuredate: string;
  reason: string;
  servicefailuretype: string;
  thd_reason: string;
  responsibledriver: string;
  comments: string;
};

/**
 * Modal editor for a Trouble Ticket — replaces the legacy side drawer with a
 * centered dialog (per the Pro 360 design). Handles both create and update
 * via the same form; status/resolve are disabled in create mode (matching the
 * legacy drawer's UX).
 */
const TroubleTicketEditModal: React.FC<TroubleTicketEditModalProps> = ({
  pronumber,
  ticket,
  initialEdits,
  lockedCategory = false,
  billTo,
  onClose,
  onSaved,
  onRescheduleApplied,
  onServiceFailureSaved,
  hasUnsavedOrderChanges,
  variant = 'modal',
  onOpenPro,
}) => {
  const isCreate = !ticket;

  // Change All — outside a ChangeAllProvider this is a disabled no-op, so the bulk
  // affordance simply never renders (e.g. the LC Resolution board's copy of this modal).
  const changeAll = useChangeAll();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkApplied, setBulkApplied] = useState(false);
  // Dispatcher (>=2) may clear a dispatched leg — driver + arrival/depart actuals —
  // across each pro's delivery dispatch group. Everyone else takes the LC path and
  // is blocked, per pro, on any group that isn't already clean. Defaults closed.
  // Dispatcher vs LC is `leg-driver-field` edit access — the same gate that already
  // decides who may assign/clear a driver, and the same one behind the
  // drop→clear-appt-dates prompt. Clearing a dispatch IS clearing a driver, so
  // reusing it keeps the two from diverging. Ships defaultDisabled, so an
  // un-granted user resolves to View and takes the LC path.
  const canClearDispatch = useCanEditDriverField();
  // The "which dates?" answer, given once for the whole set. Both boxes are
  // offered regardless of role or shape — availability is per pro and is not
  // known until the preview runs — and both start TICKED, matching the
  // single-pro dialog so the two prompts can't teach opposite habits. The
  // review modal still spells the answer out per pro before anything is
  // written. Re-asserted in openBulkFanout, which is what actually decides it.
  const [bulkChoice, setBulkChoice] = useState<RescheduleDateChoice>({
    delivery: true, prepull: true,
  });
  const isDrawer = variant === 'drawer';
  const { showToast } = useToast();
  // Set when a grid launched this editor (directly, or via a PRO drawer) and
  // wants to patch its row from the save. Null everywhere else.
  const notifyTicketSaved = useTroubleTicketSaveListener();

  const [edits, setEdits] = useState<Partial<TroubleTicket>>(() =>
    isCreate ? { status: 'NEW', driverticket: 'N', ...initialEdits } : {},
  );
  const [categories, setCategories] = useState<TroubleTicketCategory[]>([]);
  const [reasonOptions, setReasonOptions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-record history sub-drawer (edit mode only — a created ticket has a recno).
  const [historyOpen, setHistoryOpen] = useState(false);
  // Set when the create-time evaluate reports a pending reschedule: the
  // ticket has saved, but we hold the modal open to ask the user WHICH
  // appointment dates to remove before applying the side effect (phase 2).
  // Carries the whole evaluate payload: the two targets with their availability,
  // and who is en route on each.
  const [pendingReschedule, setPendingReschedule] = useState<
    {
      ttRecno: number;
      delivery: RescheduleDateTarget;
      prepull: ReschedulePrepullTarget;
      enRoute: {
        delivery: RescheduleEnRouteLeg[];
        prepull: RescheduleEnRouteLeg[];
      };
    } | null
  >(null);
  // The user's in-progress answer in that dialog. Defaults to everything
  // ticked — see the reset where the dialog opens, which is what actually
  // decides it.
  const [rescheduleChoice, setRescheduleChoice] = useState<RescheduleDateChoice>({
    delivery: true, prepull: true,
  });
  const [applyingChoice, setApplyingChoice] = useState(false);

  // ── Embedded service failure (1:1 reschedule linked to this ticket) ───────
  const isoToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  // Default the embedded SF date to the ticket's create date (date part of its
  // datetime). On create the ticket is made "now", so today IS the create
  // date; on edit we use the existing ticket's datetime.
  const ticketCreateDate = () => {
    const dt = ticket?.datetime;
    return typeof dt === 'string' && dt.length >= 10 ? dt.slice(0, 10) : isoToday();
  };
  // Staged SF fields (servicefailuredate as 'YYYY-MM-DD'). Defaults SF date to
  // the ticket create date; overwritten when an existing linked reschedule loads.
  const [sf, setSf] = useState<SfFields>(() => ({
    servicefailuredate: ticketCreateDate(), reason: '', servicefailuretype: '', thd_reason: '', responsibledriver: '', comments: '',
  }));
  // Snapshot of the SF fields as last loaded/saved, to detect SF-only edits so
  // they enable Save (and so we skip the upsert when the SF is untouched).
  const [sfBaseline, setSfBaseline] = useState<SfFields>(() => ({
    servicefailuredate: ticketCreateDate(), reason: '', servicefailuretype: '', thd_reason: '', responsibledriver: '', comments: '',
  }));
  const [linkedReschedule, setLinkedReschedule] = useState<RescheduleHistoryItem | null>(null);
  const [sfReasonOptions, setSfReasonOptions] = useState<string[]>([]);
  const [thdReasonOptions, setThdReasonOptions] = useState<string[]>([]);
  const [drivers, setDrivers] = useState<DriverLookup[]>([]);

  // Load categories once on mount.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const cats = await troubleTicketsService.listCategories(controller.signal);
        if (controller.signal.aborted) return;
        setCategories(cats);
      } catch (err: any) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        // eslint-disable-next-line no-console
        console.error('Failed to load ticket categories:', err);
      }
    })();
    return () => controller.abort();
  }, []);

  // Esc closes (matches DraggableModal semantics).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  // Service-failure dropdown options + driver lookup (for the embedded fields).
  useEffect(() => {
    const controller = new AbortController();
    schedulingService.listRescheduleReasons(controller.signal)
      .then((list) => { if (!controller.signal.aborted) setSfReasonOptions(list); })
      .catch(() => { /* leave empty */ });
    troubleTicketsService.listThdReasons(controller.signal)
      .then((list) => { if (!controller.signal.aborted) setThdReasonOptions(list); })
      .catch(() => { /* leave empty */ });
    repairOrdersService.getDrivers()
      .then((res) => { if (!controller.signal.aborted) setDrivers(res.data || []); })
      .catch(() => { /* leave empty */ });
    return () => controller.abort();
  }, []);

  // On open in edit mode, load the linked reschedule (if any) so an existing
  // service failure shows even after servicefailure flips back to N/blank.
  useEffect(() => {
    if (isCreate || !ticket) return;
    let cancelled = false;
    schedulingService.getRescheduleByTicket(ticket.recno)
      .then((r) => {
        if (cancelled || !r) return;
        setLinkedReschedule(r);
        const loaded: SfFields = {
          servicefailuredate: r.servicefailuredate ?? '',
          reason: r.reason ?? '',
          servicefailuretype: r.servicefailuretype ?? '',
          thd_reason: r.thd_reason ?? '',
          responsibledriver: r.responsibledriver ?? '',
          comments: r.comments ?? '',
        };
        setSf(loaded);
        setSfBaseline(loaded);
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [isCreate, ticket]);

  /** Effective value for a field — edited value wins, then the original ticket. */
  const getValue = useCallback(
    <K extends keyof TroubleTicket>(field: K): TroubleTicket[K] | undefined => {
      if (field in edits) return edits[field] as TroubleTicket[K];
      return ticket?.[field];
    },
    [edits, ticket],
  );

  const setField = useCallback(
    <K extends keyof TroubleTicket>(field: K, value: TroubleTicket[K]) => {
      setEdits((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const currentDriverTicket = (getValue('driverticket') || 'N') as string;
  const currentCategory = (getValue('category') || '') as string;
  const currentStatus = (getValue('status') || '') as string;
  // Default to '' (the "—" option) so new tickets must explicitly resolve
  // preventable before going to RESOLVED. `??` preserves stored '' on edit
  // rather than coercing it to 'N' like `||` did.
  const currentPreventable = (getValue('preventable') ?? '') as string;
  const currentAssignedTo = ((getValue('assignedto') || '') as string).toUpperCase();
  const currentIssue = (getValue('issue') || '') as string;
  const currentResolve = (getValue('resolve') || '') as string;
  const currentReason = (getValue('reason') || '') as string;
  // Default to '' (the "—" option) for new tickets rather than 'N'. The
  // backend ServiceFailureType allows '' and the troubleticket column is
  // NOT NULL char(1), so empty string is what's actually stored.
  const currentServiceFailure = getValue('servicefailure') ?? '';
  const isResolved = currentStatus.toUpperCase() === 'RESOLVED';

  // ── Maintenance pings on the linked repair order ─────────────────────────
  // A REPAIR ORDER ticket is auto-created with the RO number written into its
  // issue text; that number is the ONLY link back (troubleticket has no
  // repairorderrecno, and pronumber can't say which of a PRO's repair orders
  // this is). Read it off the SAVED ticket, not the live edit: an LC mid-way
  // through retyping the issue must not swing the controls in and out. A
  // hand-written ticket parses to null and gets no controls at all.
  const linkedRoRecno = useMemo(
    () => (ticket ? repairOrderRecnoForTicket(ticket) : null),
    [ticket],
  );
  const pingRoRecnos = useMemo(
    () => (linkedRoRecno === null ? [] : [linkedRoRecno]),
    [linkedRoRecno],
  );
  // Read off the SAVED ticket first, for the same reason as the RO number: the
  // repair orders are looked up by PRO, and the ticket's own PRO is the one its
  // repair order belongs to.
  const pingPronumber = ticket?.pronumber ?? pronumber;
  const {
    pingsByRo,
    cardsByRo,
    requestPing: requestRepairOrderPing,
  } = useRepairOrderPings(pingPronumber, pingRoRecnos);
  // No card for the parsed recno — deleted repair order, or one raised against
  // another PRO — means we know nothing about it, so render no controls.
  const linkedRoCard = linkedRoRecno === null ? undefined : cardsByRo[linkedRoRecno];

  // Block creating a "Reschedule Requested" ticket while the order has unsaved
  // edits: the create-time reschedule side effect reads the order's SAVED
  // state, so pending changes would be missed (and the wrong dates cleared).
  // Create-only — editing a ticket never triggers the side effect.
  const isRescheduleRequested =
    currentCategory.trim().toUpperCase() === 'RESCHEDULE REQUESTED';
  const blockRescheduleForUnsavedChanges =
    isCreate && isRescheduleRequested && !!hasUnsavedOrderChanges;

  // ── Embedded service-failure derivations ─────────────────────────────────
  const effectiveBillTo = billTo ?? ticket?.billto ?? null;
  const isThdBillTo =
    effectiveBillTo !== null &&
    effectiveBillTo !== undefined &&
    THD_BILLTOS.includes(effectiveBillTo);
  const effectivePro = isCreate ? pronumber : (ticket?.pronumber ?? pronumber);
  // THD Reason only applies when the ticket has a pro and that order is a THD billto.
  const showThdReason = !!effectivePro && isThdBillTo;
  // Show the embedded service-failure fields when the ticket is flagged Y, or
  // once a reschedule has been linked (so it persists after Y → N/blank).
  const showSfSection = currentServiceFailure === 'Y' || linkedReschedule !== null;
  // SF fields changed from what was loaded/saved — lets SF-only edits enable
  // Save and lets us skip the upsert when the SF is untouched.
  const sfDirty =
    sf.servicefailuredate !== sfBaseline.servicefailuredate ||
    sf.reason !== sfBaseline.reason ||
    sf.servicefailuretype !== sfBaseline.servicefailuretype ||
    sf.thd_reason !== sfBaseline.thd_reason ||
    sf.responsibledriver !== sfBaseline.responsibledriver ||
    sf.comments !== sfBaseline.comments;
  // The embedded SF carries a controllable (C:) reason with no type. Blocks the
  // single-pro save AND the fan-out: an untyped C: row never counts against
  // OTD, and the backend refuses it too. Same rule as the Service Failures
  // modal and the TTs & Service Failures board — the type follows the reason.
  const sfTypeBlocked = showSfSection && serviceFailureTypeMissing(sf.reason, sf.servicefailuretype);

  // Driver options keyed by unit number (what we store in responsibledriver).
  const driverOptions = useMemo<TypeAheadOption[]>(
    () =>
      drivers
        .filter((d) => d.unitnumber)
        .map((d) => {
          const toTitleCase = (s: string) =>
            s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
          const name = toTitleCase(`${d.firstname} ${d.lastname}`.trim());
          return {
            value: d.unitnumber,
            label: name ? `${d.unitnumber} - ${name}` : d.unitnumber,
            description: d.active ? undefined : '⚠ Inactive',
            group: d.active ? 'Active' : 'Inactive',
          };
        }),
    [drivers],
  );
  // Filter category list by driverticket flag + THD eligibility (lifted from
  // legacy drawer lines 90-104).
  const filteredCategories = useMemo(() => {
    const isDriver = currentDriverTicket === 'Y';
    const effectiveBillTo = billTo ?? ticket?.billto ?? null;
    const isTHD =
      effectiveBillTo !== null &&
      effectiveBillTo !== undefined &&
      THD_BILLTOS.includes(effectiveBillTo);

    return categories.filter((cat) => {
      const typeMatch = isDriver
        ? cat.driverticket === 'Y' || cat.driverticket === 'BOTH'
        : cat.driverticket === 'N' || cat.driverticket === 'BOTH';
      if (cat.category.startsWith('THD -') && !isTHD) return false;
      return typeMatch;
    });
  }, [categories, currentDriverTicket, billTo, ticket?.billto]);

  const showReasonField = categoryHasReasonList(currentCategory);

  // Load the reason dropdown options whenever the category changes.
  useEffect(() => {
    if (!showReasonField) {
      setReasonOptions([]);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const reasons = await troubleTicketsService.listReasonsByCategory(
          currentCategory,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setReasonOptions(reasons);
      } catch (err: any) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        // eslint-disable-next-line no-console
        console.error('Failed to load ticket reasons:', err);
        setReasonOptions([]);
      }
    })();
    return () => controller.abort();
  }, [currentCategory, showReasonField]);

  const hasChanges = isCreate
    ? !!(currentCategory.trim() && currentAssignedTo.trim() && currentIssue.trim())
    : Object.keys(edits).length > 0 || (showSfSection && sfDirty);

  // --- Change All: bulk CREATE this ticket across the target set --------------
  // CREATE only — bulk edit and bulk delete of tickets/service failures are out of
  // scope, so the affordance never renders on an existing ticket.
  const canBulkCreate = isCreate && changeAll.enabled && changeAll.targetPros.length > 0;
  // Reschedule Requested fans out: its one question — whether to remove the
  // scheduled date — is asked ONCE before the review modal and applied to every pro,
  // and the review modal spells out the answer so no date is cleared unseen. Per-pro
  // eligibility (an LC whose group is still dispatched, a locked leg, no date) is
  // decided by the backend, which re-checks it on both preview and apply.
  // The unsaved-order guard applies to the fan-out too, and more so: the reschedule
  // is generated from each pro's SAVED details, so staged edits on the anchor would be
  // missed on every pro in the set. Create Ticket already blocks on this; the bulk
  // button used to bypass it.
  const blockedReason = blockRescheduleForUnsavedChanges
    ? RESCHEDULE_UNSAVED_ORDER_MESSAGE
    : bulkBlockedReason({ hasChanges, sfTypeMissing: sfTypeBlocked });

  const bulkTicket = useMemo(() => buildBulkTicket({
    category: currentCategory,
    issue: currentIssue,
    assignedto: currentAssignedTo,
    status: edits.status as string | undefined,
    driverticket: edits.driverticket as string | undefined,
    preventable: currentPreventable as string | null,
    reason: currentReason,
    servicefailure: currentServiceFailure as string | null,
  }), [
    currentCategory, currentIssue, currentAssignedTo, edits.status, edits.driverticket,
    currentPreventable, currentReason, currentServiceFailure,
  ]);

  // Reschedule Requested asks its one question BEFORE the review modal, so the answer
  // is already fixed when the per-pro preview runs. Every other category goes straight
  // to review.
  const [rescheduleAsk, setRescheduleAsk] = useState(false);
  const openBulkFanout = () => {
    setBulkChoice({ delivery: true, prepull: true });
    if (isRescheduleRequested) {
      setRescheduleAsk(true);
      return;
    }
    setBulkOpen(true);
  };
  const answerReschedule = () => {
    setRescheduleAsk(false);
    setBulkOpen(true);
  };

  const bulkSf = useMemo(() => buildBulkSf({
    showSfSection,
    showThdReason,
    servicefailuredate: sf.servicefailuredate,
    reason: sf.reason,
    servicefailuretype: sf.servicefailuretype,
    thd_reason: sf.thd_reason,
    comments: sf.comments,
  }), [showSfSection, showThdReason, sf.servicefailuredate, sf.reason, sf.servicefailuretype, sf.thd_reason, sf.comments]);

  const handleSave = useCallback(async () => {
    if (!isCreate && !ticket) return;
    // Nothing to do only when neither the ticket nor its service failure changed.
    if (!isCreate && Object.keys(edits).length === 0 && !(showSfSection && sfDirty)) return;

    // Can't create a Reschedule Requested ticket with unsaved order edits — the
    // reschedule side effect would run against stale (saved) order data.
    if (blockRescheduleForUnsavedChanges) {
      setError(RESCHEDULE_UNSAVED_ORDER_MESSAGE);
      return;
    }

    // Field validation matches the legacy drawer.
    const missing: string[] = [];
    if (!currentCategory.trim()) missing.push('Category');
    if (!currentAssignedTo.trim()) missing.push('Assigned To');
    if (!currentIssue.trim()) missing.push('Issue');
    if (showReasonField && !currentReason.trim()) missing.push('Reason');
    if (missing.length > 0) {
      setError(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`);
      return;
    }
    if (isResolved && !currentResolve.trim()) {
      setError('Resolution text is required when status is Resolved.');
      return;
    }
    // V1 (lib_editrecord.pl:21619-21622): preventable must be set when
    // status = RESOLVED.
    if (isResolved && !currentPreventable.trim()) {
      setError('Preventable (Y/N) is required when status is Resolved.');
      return;
    }
    // Service-failure / reschedule fields are intentionally NOT required here:
    // a user can save the ticket with the SF flagged but its details blank, and
    // complete them later from the TTs & Service Failures board. The one
    // exception is the type on a controllable (C:) reason — checked BEFORE the
    // ticket write, since the SF is saved after it and a late refusal would
    // leave a ticket with no service failure.
    if (sfTypeBlocked) {
      setError(SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let savedTtRecno: number | undefined;
      let sfWritten = false;

      // Persists the embedded service failure (1:1 reschedule) for a ticket —
      // creating a blank shell when SF=Y with no details entered. Fields may be
      // blank; the user completes them later on the board. THD reason only goes
      // out for THD-pro tickets.
      const saveEmbeddedSf = async (ttRecno: number) => {
        await schedulingService.upsertRescheduleForTicket(ttRecno, {
          servicefailuredate: sf.servicefailuredate || null,
          reason: sf.reason,
          servicefailuretype: sf.servicefailuretype,
          thd_reason: showThdReason ? sf.thd_reason : '',
          responsibledriver: sf.responsibledriver,
          comments: sf.comments,
        });
        sfWritten = true;
        // Let the pro refresh its Service Failures count + panel.
        onServiceFailureSaved?.();
      };

      // Snapshot of what this save wrote, for a launching grid's optimistic
      // row patch. Built lazily so it reflects the SF write when there was one.
      const saveResult = (): TroubleTicketSaveResult => ({
        pronumber,
        recno: savedTtRecno,
        created: isCreate,
        ticket: {
          category: currentCategory,
          status: currentStatus,
          assignedto: currentAssignedTo,
          issue: currentIssue,
          resolve: currentResolve,
          preventable: currentPreventable,
          driverticket: currentDriverTicket,
          servicefailure: String(currentServiceFailure ?? ''),
        },
        serviceFailure: sfWritten
          ? {
              servicefailuredate: sf.servicefailuredate || null,
              reason: sf.reason,
              servicefailuretype: sf.servicefailuretype,
              thd_reason: showThdReason ? sf.thd_reason : '',
              responsibledriver: sf.responsibledriver,
              comments: sf.comments,
            }
          : null,
      });

      if (isCreate) {
        const result = await troubleTicketsService.create({
          pronumber,
          category: currentCategory,
          issue: currentIssue,
          assignedto: currentAssignedTo,
          status: (edits.status as string) || 'NEW',
          driverticket: (edits.driverticket as 'Y' | 'N') || 'N',
          preventable: currentPreventable as 'Y' | 'N' | '',
          reason: currentReason,
          billto: billTo ?? null,
          servicefailure: currentServiceFailure,
        });
        savedTtRecno = result.ticket?.recno;

        // Persist the embedded SF FIRST (when flagged), so it's the
        // ticket-linked reschedule row that the Reschedule Requested side
        // effect MERGES its cleared delivery date into — rather than that side
        // effect inserting a second row and the user's entries being lost.
        if (showSfSection && savedTtRecno) {
          await saveEmbeddedSf(savedTtRecno);
        }

        // Server-side reschedule side effect for this category runs in two
        // phases. Phase 1 (evaluate) happened on create and told us whether
        // a reschedule would apply. Case-insensitive to match the backend
        // gate.
        if (currentCategory.trim().toUpperCase() === 'RESCHEDULE REQUESTED') {
          const trigger = result.rescheduleTrigger;
          if (trigger && trigger.applied === false) {
            if ('pending_confirmation' in trigger) {
              // At least one date is clearable — refresh the ticket list now (the
              // ticket + embedded SF are saved) but keep the modal open to ask
              // WHICH dates to remove. Phase 2 (handleRescheduleChoice →
              // applyReschedule) merges the cleared delivery date into the
              // embedded row.
              onSaved();
              notifyTicketSaved?.(saveResult());
              // Both boxes start TICKED. Removing the dates is the whole point
              // of a Reschedule Requested, and an all-unticked prompt was being
              // clicked straight through — the ticket saved, nothing cleared,
              // and the user came back asking why. Unticking is now the
              // deliberate act. Only a target the server marks AVAILABLE is
              // pre-ticked: the other one is never rendered, so a true there
              // would be an answer the user can neither see nor retract, and it
              // would wrongly arm Apply on a `writesNothing` prompt.
              setRescheduleChoice({
                delivery: trigger.delivery.available,
                prepull: trigger.prepull.available,
              });
              setPendingReschedule({
                // Carried into phase 2 so the reschedule row links back to
                // this ticket (reschedule.troubleticketrecno).
                ttRecno: savedTtRecno ?? 0,
                delivery: trigger.delivery,
                prepull: trigger.prepull,
                enRoute: trigger.en_route,
              });
              setSaving(false);
              return;
            }
            // Gate skipped before any prompt — surface why so the user
            // isn't left wondering what (didn't) happen on the order.
            showToast({
              type: 'info',
              message: RESCHEDULE_SKIP_MESSAGES[trigger.skip_reason]
                ?? 'Trouble ticket saved, but the reschedule side effect did not run.',
            });
          }
          try {
            await onRescheduleApplied?.();
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('onRescheduleApplied callback failed:', e);
          }
        }
      } else {
        savedTtRecno = ticket!.recno;
        // Only update the ticket when its own fields changed — an SF-only edit
        // saves the linked reschedule and leaves the ticket untouched.
        if (Object.keys(edits).length > 0) {
          const payload: Partial<TroubleTicket> = { ...edits };
          if ('category' in edits && billTo !== undefined) {
            payload.billto = billTo;
          }
          await troubleTicketsService.update(ticket!.recno, payload);
        }
        // Persist the embedded service failure (fields may be blank). On edit
        // there's no Reschedule Requested side effect, so this is the only
        // write to the linked reschedule.
        if (showSfSection && savedTtRecno && (sfDirty || !linkedReschedule)) {
          await saveEmbeddedSf(savedTtRecno);
        }
      }

      onSaved();
      notifyTicketSaved?.(saveResult());
      onClose();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error saving ticket:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to save ticket');
    } finally {
      setSaving(false);
    }
  }, [
    isCreate,
    ticket,
    edits,
    currentCategory,
    currentAssignedTo,
    currentIssue,
    currentReason,
    currentResolve,
    currentServiceFailure,
    currentPreventable,
    currentStatus,
    currentDriverTicket,
    showReasonField,
    isResolved,
    blockRescheduleForUnsavedChanges,
    pronumber,
    billTo,
    showSfSection,
    showThdReason,
    sf,
    sfDirty,
    sfTypeBlocked,
    linkedReschedule,
    onSaved,
    onClose,
    onRescheduleApplied,
    onServiceFailureSaved,
    notifyTicketSaved,
    showToast,
  ]);

  /**
   * Phase 2 of the Reschedule Requested side effect: apply the user's answer to
   * the "which dates?" prompt, then close.
   *   delivery → clear the delivery group's appt dates and record the reschedule.
   *   prepull  → clear the pickup leg's appointment (and, for a dispatcher, its
   *              driver + actuals). Records no reschedule — that row tracks the
   *              DELIVERY date moving.
   * Neither is still an action for a dispatcher: the delivery group's driver and
   * arrival/departure times are reset so the dispatch can be re-worked.
   */
  const handleRescheduleChoice = useCallback(
    async (choice: RescheduleDateChoice) => {
      setApplyingChoice(true);
      try {
        const res = await troubleTicketsService.applyReschedule(
          pronumber,
          {
            removeScheduledDate: choice.delivery,
            removePrepullDate: choice.prepull,
          },
          pendingReschedule?.ttRecno ?? 0,
        );
        if (res.applied) {
          if (res.mode === 'prepull_only') {
            showToast({
              type: 'success',
              message:
                `The pre-pull date${res.prepull_date ? ` (${res.prepull_date})` : ''} was removed. `
                + 'No reschedule was recorded — that tracks the delivery date.',
            });
          } else if (res.mode === 'kept_date') {
            showToast({
              type: 'info',
              message:
                'Driver and arrival/departure times were cleared. The scheduled date was kept in place and no reschedule was recorded.',
            });
          } else if (res.duplicate) {
            showToast({
              type: 'info',
              message:
                'A reschedule for this delivery date already exists — the appointment was cleared but no new reschedule record was created.',
            });
          } else {
            showToast({
              type: 'success',
              message: 'Scheduled date removed and the reschedule was recorded.',
            });
          }
          // Downstream legs that kept their dates. The clear fans out past the
          // dispatch group (leg_appt_guard.plan_downstream_appt_clears), but a
          // leg already in motion holds its appointment — and a PARTIAL clear
          // leaves the pro in Missing Appointment, so say so rather than let it
          // reappear on the LC board unexplained. Same wording as the grid's
          // reschedule modal.
          // The pre-pull half of a combined answer. Reported separately so a
          // delivery success never implies the pre-pull went with it.
          if (res.mode !== 'prepull_only' && res.prepull_cleared) {
            showToast({
              type: 'success',
              message:
                `The pre-pull date${res.prepull_date ? ` (${res.prepull_date})` : ''} was also removed.`,
            });
          }
          if (res.prepull_skipped_reason) {
            showToast({ type: 'warning', message: `Pre-pull date kept — ${res.prepull_skipped_reason}` });
          }
          if (res.kept_appt_legs?.length) {
            showToast({
              type: 'warning',
              message:
                'Keeping the appointment on ' +
                res.kept_appt_legs
                  .map(
                    (l) =>
                      `leg ${l.legnum} (${DOWNSTREAM_SKIP_REASON_LABELS[l.reason]})`,
                  )
                  .join(', ') +
                '. This pro will stay in Missing Appointment until those are cleared.',
            });
          }
        } else {
          // Gate skipped between evaluate and apply (e.g. a leg just became
          // paid) — surface the same reason copy.
          showToast({
            type: 'info',
            message: RESCHEDULE_SKIP_MESSAGES[res.skip_reason]
              ?? 'The reschedule side effect did not run.',
          });
        }
        try {
          await onRescheduleApplied?.();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('onRescheduleApplied callback failed:', e);
        }
        // Reconcile-only: the ticket itself is unchanged here, but this wrote a
        // reschedule row and cleared dates on the order, so a launching grid
        // needs fresh server data rather than a patch.
        notifyTicketSaved?.();
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to apply reschedule choice:', e);
        showToast({
          type: 'error',
          message:
            'The trouble ticket saved, but applying the reschedule choice failed. Please retry from the order.',
        });
      } finally {
        setApplyingChoice(false);
        setPendingReschedule(null);
        onClose();
      }
    },
    [pronumber, pendingReschedule, onRescheduleApplied, notifyTicketSaved, onClose, showToast],
  );

  const title = isCreate
    ? 'New Trouble Ticket'
    : ticket
      ? `Trouble Ticket #${ticket.recno}`
      : 'Trouble Ticket';

  // Rendered only while open. Alt+S saves the ticket, Alt+C closes. Both go
  // unbound during the phase-2 reschedule confirmation below — that's a separate
  // question with its own buttons, and the ticket has already saved by then.
  useAltHotkeys(
    {
      S: saving || applyingChoice || pendingReschedule ? null : handleSave,
      C: saving || applyingChoice || pendingReschedule ? null : onClose,
    },
    undefined,
    { layer: true },
  );

  // Two overlays, one drag instance each, keyed to their own visibility so a
  // drag on one never leaks an offset onto the other. The main panel is only
  // draggable in modal mode — the drawer variant is a docked right-side panel.
  const confirmDrag = useModalDrag(!!pendingReschedule);
  const mainDrag = useModalDrag(!pendingReschedule);

  // Phase-2 confirmation: the ticket has saved and at least one appointment
  // date is clearable. Ask WHICH before clearing anything.
  if (pendingReschedule) {
    const { delivery, prepull, enRoute } = pendingReschedule;
    const chosen = rescheduleChoice;
    // "Neither" is a real answer, not a cancel: for a dispatcher it still resets
    // the delivery group's driver and arrival/departure times. For an LC it
    // writes nothing, so Apply would be a round trip to do nothing.
    const writesNothing =
      !chosen.delivery && !chosen.prepull && !(canClearDispatch && delivery.available);
    // The stamps this answer is about to destroy. The delivery group's go
    // whatever the date answer is (for a dispatcher); the pre-pull scope's only
    // if that box is ticked.
    const losingStamps = [
      ...(delivery.available ? enRoute.delivery : []),
      ...(chosen.prepull ? enRoute.prepull : []),
    ];

    return createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/40"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reschedule-confirm-title"
      >
        <div
          ref={confirmDrag.panelRef}
          style={confirmDrag.panelStyle}
          className="bg-panel rounded-lg shadow-2xl w-full max-w-lg mx-4 flex flex-col border border-slate-200 dark:border-gray-700"
        >
          <ModalHeader
            title="Which appointment dates should be removed?"
            titleId="reschedule-confirm-title"
            dragHandleProps={confirmDrag.dragHandleProps}
          />
          <div className="px-5 py-4 space-y-3 text-sm text-label">
            <p className="text-xs text-muted">
              These dates will be removed. Untick any you want to keep. Only the
              dates this order actually has are listed.
            </p>

            {/* Each target is rendered only when the server says it is on offer —
                availability depends on the order's shape AND the user's role, so
                the client never has to work it out for itself. */}
            {delivery.available && (
              <label className="flex items-start gap-3 rounded border border-slate-200 dark:border-gray-700 px-3 py-2 cursor-pointer hover:bg-highlight">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={chosen.delivery}
                  disabled={applyingChoice}
                  onChange={(e) =>
                    setRescheduleChoice((c) => ({ ...c, delivery: e.target.checked }))}
                />
                <span>
                  <span className="font-medium text-body">
                    {RESCHEDULE_DELIVERY_LABEL}
                  </span>
                  {delivery.date && (
                    <span className="font-semibold text-body"> — {delivery.date}</span>
                  )}
                  <span className="block text-xs text-muted">
                    Clears the appointment dates across the delivery dispatch group
                    and records the reschedule.
                  </span>
                </span>
              </label>
            )}

            {prepull.available && (
              <label className="flex items-start gap-3 rounded border border-slate-200 dark:border-gray-700 px-3 py-2 cursor-pointer hover:bg-highlight">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={chosen.prepull}
                  disabled={applyingChoice}
                  onChange={(e) =>
                    setRescheduleChoice((c) => ({ ...c, prepull: e.target.checked }))}
                />
                <span>
                  <span className="font-medium text-body">
                    {RESCHEDULE_PREPULL_LABEL}
                  </span>
                  {prepull.date && (
                    <span className="font-semibold text-body"> — {prepull.date}</span>
                  )}
                  <span className="block text-xs text-muted">
                    {canClearDispatch
                      ? `Clears the appointment on leg ${prepull.legnum} and resets its `
                        + 'driver and arrival/departure times. No reschedule is recorded '
                        + 'for the pre-pull on its own.'
                      : `Clears the appointment on leg ${prepull.legnum}. No reschedule `
                        + 'is recorded for the pre-pull on its own.'}
                  </span>
                </span>
              </label>
            )}

            {/* Why a target the user might expect isn't here. Silence would read
                as "this order has no pre-pull" even when it has one that is
                simply already paid, departed, or still dispatched. */}
            {!prepull.available && prepull.reason && (
              <p className="text-xs text-muted">
                No pre-pull date to remove — {prepull.reason}
              </p>
            )}

            {/* The dispatcher's reset happens whatever the dates answer, so say
                so plainly rather than hiding it behind the delivery checkbox. */}
            {canClearDispatch && delivery.available && (
              <p className="text-xs text-muted">
                The driver and arrival/departure times on the delivery dispatch
                group are cleared either way, so the dispatch can be re-worked.
              </p>
            )}

            {/* THE WARNING: a driver who has physically moved is about to lose
                the stamps proving it. Nothing re-derives them. Informational —
                the user can read it and proceed. */}
            {losingStamps.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-950">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                  {losingStamps.length === 1
                    ? 'A driver on this order shows en route'
                    : 'Drivers on this order show en route'}
                </p>
                <ul className="mt-1 list-disc pl-5 text-xs text-amber-800 dark:text-amber-200">
                  {losingStamps.map((l) => (
                    <li key={`${l.legnum}-${l.when}`}>
                      Leg {l.legnum}
                      {l.driver ? ` — driver ${l.driver}` : ''} {l.status} at {l.when}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                  Their arrival and departure times will be cleared and cannot be
                  recovered. You can continue if that is what you intend.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 rounded-b-lg">
            <button
              type="button"
              onClick={() => {
                setPendingReschedule(null);
                onClose();
              }}
              disabled={applyingChoice}
              className="px-4 py-2 text-sm font-medium text-label bg-panel border border-gray-300 rounded hover:bg-highlight dark:border-gray-600 disabled:opacity-50"
            >
              {writesNothing ? 'Close' : 'Cancel — change nothing'}
            </button>
            <button
              type="button"
              onClick={() => handleRescheduleChoice(chosen)}
              disabled={applyingChoice || writesNothing}
              title={writesNothing ? 'Tick a date to remove' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {applyingChoice && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              Apply
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex bg-scrim/40 ${
        isDrawer ? 'justify-end' : 'items-center justify-center'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trouble-ticket-modal-title"
      onMouseDown={(e) => {
        // Close on backdrop click only (not when dragging from inside the panel).
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        ref={mainDrag.panelRef}
        style={mainDrag.panelStyle}
        className={
          isDrawer
            ? 'bg-panel shadow-2xl w-full max-w-lg h-full flex flex-col border-l border-slate-200 dark:border-gray-700'
            : 'bg-panel rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col border border-slate-200 dark:border-gray-700'
        }
      >
        <ModalHeader
          titleId="trouble-ticket-modal-title"
          title={
            <span className="flex items-baseline gap-3 min-w-0">
              {title}
              {/* Drawer opened by recno: surface the PRO so the user can jump to
                  the order. Hidden on booking-only tickets (pronumber 0) and in
                  modal mode (which always opens from the pro itself). */}
              {isDrawer && onOpenPro && !!effectivePro && (
                <span className="flex items-center gap-1 text-sm font-normal">
                  <button
                    type="button"
                    onClick={() => onOpenPro(effectivePro)}
                    className="text-blue-300 hover:text-blue-200 hover:underline font-medium"
                  >
                    PRO {effectivePro}
                  </button>
                  <a
                    href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${effectivePro}&searchtype=pronumber`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-slate-300 hover:text-white"
                    title="Open in legacy CMS"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                </span>
              )}
            </span>
          }
          onClose={saving ? undefined : onClose}
          dragHandleProps={isDrawer ? undefined : mainDrag.dragHandleProps}
          actions={
            /* Quick view of this ticket's change history. Edit mode only —
               a created ticket has a recno to scope the history by. */
            !isCreate && ticket ? (
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-300 hover:text-white"
                title="View change history"
              >
                <ClockIcon className="h-4 w-4" />
                History
              </button>
            ) : undefined
          }
        />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300"
                aria-label="Dismiss error"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Surfaced as soon as "Reschedule Requested" is selected with unsaved
              order edits, so the user can fix it before filling in the ticket. */}
          {blockRescheduleForUnsavedChanges && (
            <div className="p-3 rounded bg-amber-50 border border-amber-200 dark:bg-amber-900/30 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {RESCHEDULE_UNSAVED_ORDER_MESSAGE}
              </p>
            </div>
          )}

          {ticket && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              <div>
                <div className="font-medium text-muted">Created By</div>
                <div className="text-body mt-0.5">{ticket.userid || '-'}</div>
              </div>
              <div>
                <div className="font-medium text-muted">Date/Time</div>
                <div className="text-body mt-0.5">
                  {ticket.datetime
                    ? new Date(ticket.datetime).toLocaleString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          <div>
            <label
              htmlFor="tt-status"
              className="block text-xs font-medium text-muted mb-1"
            >
              Status
            </label>
            <select
              id="tt-status"
              value={currentStatus}
              onChange={(e) => setField('status', e.target.value)}
              disabled={saving || isCreate}
              className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isCreate ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-900'
              }`}
            >
              <option value="">-</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Driver Ticket / Preventable */}
          <div className="grid grid-cols-2 gap-x-6">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                Driver Ticket
              </label>
              <select
                value={currentDriverTicket}
                onChange={(e) => {
                  const next = e.target.value as 'Y' | 'N';
                  setField('driverticket', next);
                  // If category no longer matches the new filter, clear it — unless it
                  // is pinned, in which case the whole point is that it survives. (The
                  // THD categories are all driverticket='BOTH' today, so this is a
                  // guard against a future category list, not a live bug.)
                  if (lockedCategory) return;
                  const match = categories.find((c) => c.category === currentCategory);
                  if (match) {
                    const stillValid =
                      next === 'Y'
                        ? match.driverticket === 'Y' || match.driverticket === 'BOTH'
                        : match.driverticket === 'N' || match.driverticket === 'BOTH';
                    if (!stillValid) {
                      setField('category', '');
                      setField('reason', '');
                    }
                  }
                }}
                disabled={saving}
                aria-label="Driver Ticket"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                Preventable
                {isResolved && <span className="text-red-500 ml-1">*</span>}
              </label>
              <select
                value={currentPreventable}
                onChange={(e) => setField('preventable', e.target.value as 'Y' | 'N' | '')}
                disabled={saving}
                aria-label="Preventable"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">—</option>
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              Category<span className="text-red-500 ml-1">*</span>
            </label>
            <TypeAhead
              options={filteredCategories.map((cat) => ({ value: cat.category, label: cat.category }))}
              value={currentCategory}
              onChange={(next) => {
                setField('category', next);
                // Clear reason if the new category has no reason list.
                if (!categoryHasReasonList(next)) {
                  setField('reason', '');
                }
              }}
              onClear={() => {
                setField('category', '');
                setField('reason', '');
              }}
              placeholder="Select category…"
              disabled={saving || lockedCategory}
              showSearch
              flagInvalidValue
            />
            {lockedCategory && (
              <p className="mt-1 text-xs text-muted">
                Fixed for this prompt — close without saving if no ticket is needed.
              </p>
            )}
          </div>

          {/* Reason — shown only for THD tickets. Options come from
              /trouble-tickets/reasons-by-category (thdstatusreason). */}
          {showReasonField && (
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                Reason<span className="text-red-500 ml-1">*</span>
              </label>
              <TypeAhead
                options={reasonOptions.map((r) => ({ value: r, label: r }))}
                value={currentReason}
                onChange={(next) => setField('reason', next)}
                onClear={() => setField('reason', '')}
                placeholder="Select reason…"
                disabled={saving}
                showSearch
                flagInvalidValue
              />
            </div>
          )}

          {/* Assigned To */}
          <div>
            <label
              htmlFor="tt-assignedto"
              className="block text-xs font-medium text-muted mb-1"
            >
              Assigned To<span className="text-red-500 ml-1">*</span>
            </label>
            <select
              id="tt-assignedto"
              value={currentAssignedTo}
              onChange={(e) => setField('assignedto', e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-</option>
              {ASSIGNED_TO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Issue */}
          <div>
            <label
              htmlFor="tt-issue"
              className="block text-xs font-medium text-muted mb-1"
            >
              Issue<span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id="tt-issue"
              value={currentIssue}
              onChange={(e) => setField('issue', e.target.value)}
              disabled={saving}
              rows={3}
              placeholder="Describe the issue..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
            {/* Ask maintenance for a vendor ETA / a completion status on the
                repair order this ticket was raised for — the same control the
                ROs sub-tab offers, so an LC never has to leave this modal to
                send the email it replaces. */}
            {linkedRoRecno !== null && linkedRoCard && (
              <RepairOrderPingControls
                pings={pingsByRo[linkedRoRecno]}
                allowRequest
                orderStatus={linkedRoCard.orderStatus}
                onRequest={(requestType) => {
                  void requestRepairOrderPing(linkedRoRecno, requestType);
                }}
                showHandle
                size="roomy"
              />
            )}
          </div>

          {/* Resolution */}
          <div>
            <label
              htmlFor="tt-resolve"
              className="block text-xs font-medium text-muted mb-1"
            >
              Resolution
              {isResolved && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              id="tt-resolve"
              value={currentResolve}
              onChange={(e) => setField('resolve', e.target.value)}
              disabled={saving || isCreate}
              rows={3}
              placeholder={
                isCreate ? 'Available after ticket is created' : 'Describe the resolution...'
              }
              className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                isCreate ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-900'
              }`}
            />
            {ticket?.resolvedatetime && (
              <p className="mt-1 text-xs text-muted">
                Resolved:{' '}
                {new Date(ticket.resolvedatetime).toLocaleString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </p>
            )}
          </div>

          {/* Service Failure — kept directly above the Service Failure Details
              so the flag and its detail fields stay together. */}
          <div>
            <label
              htmlFor="tt-servicefailure"
              className="block text-xs font-medium text-muted mb-1"
            >
              Service Failure
            </label>
            <select
              id="tt-servicefailure"
              value={currentServiceFailure}
              onChange={(e) => setField('servicefailure', e.target.value as 'Y' | 'N' | '')}
              disabled={saving}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-</option>
              {/* Y requires a pro — a service failure is always tied to one.
                  Hidden on booking-only tickets, except to display a legacy Y. */}
              {(!!effectivePro || currentServiceFailure === 'Y') && <option value="Y">Y</option>}
              <option value="N">N</option>
            </select>
            {!effectivePro && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                A service failure requires a PRO — not available on booking-only tickets.
              </p>
            )}
          </div>

          {/* Embedded service failure (1:1 reschedule). Shown when the ticket
              is flagged Y, or once a reschedule has been linked. Placed at the
              bottom, beneath the Service Failure flag. */}
          {showSfSection && (
            <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-3 space-y-3">
              <div className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                Service Failure Details
                {linkedReschedule && (
                  <span className="ml-2 font-normal text-amber-700/70 dark:text-amber-400/70">
                    #{linkedReschedule.recno}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Service Failure Date
                </label>
                <DateTimePicker
                  mode="date"
                  selectionType="single"
                  inputVariant="modal"
                  usePortal
                  autoApply
                  labelType="none"
                  value={(() => {
                    const v = sf.servicefailuredate;
                    if (!v) return null;
                    const [y, m, d] = v.split('-').map(Number);
                    return Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
                      ? new Date(y, m - 1, d)
                      : null;
                  })()}
                  onChange={(val) => {
                    const d = val instanceof Date ? val : null;
                    const iso = d
                      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                      : '';
                    setSf((p) => ({ ...p, servicefailuredate: iso }));
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Reason
                </label>
                <TypeAhead
                  options={sfReasonOptions.map((r) => ({ value: r, label: r }))}
                  value={sf.reason}
                  onChange={(next) => setSf((p) => ({ ...p, reason: next }))}
                  onClear={() => setSf((p) => ({ ...p, reason: '' }))}
                  placeholder="Select reason…"
                  disabled={saving}
                  showSearch
                  flagInvalidValue
                />
              </div>

              {/* What failed. Required once the reason is controllable — only
                  Delivery Failures count against OTD, so an untyped C: row is a
                  hole (handleSave refuses it, as do the other SF editors and the
                  backend). Optional otherwise: the rest of the SF may still be
                  completed later on the TTs & Service Failures board. */}
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-xs font-medium text-muted">
                    Service Failure Type
                    {isControllableReason(sf.reason) && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <ServiceFailureTypeHelp />
                </div>
                <TypeAhead
                  options={SERVICE_FAILURE_TYPE_OPTIONS}
                  value={sf.servicefailuretype}
                  onChange={(next) => setSf((p) => ({ ...p, servicefailuretype: next }))}
                  onClear={() => setSf((p) => ({ ...p, servicefailuretype: '' }))}
                  placeholder="Select type…"
                  disabled={saving}
                  showSearch
                  showDescriptions
                  flagInvalidValue
                />
              </div>

              {showThdReason && (
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">
                    THD Reason
                  </label>
                  <TypeAhead
                    options={thdReasonOptions.map((r) => ({ value: r, label: r }))}
                    value={sf.thd_reason}
                    onChange={(next) => setSf((p) => ({ ...p, thd_reason: next }))}
                    onClear={() => setSf((p) => ({ ...p, thd_reason: '' }))}
                    placeholder="Select THD reason…"
                    disabled={saving}
                    showSearch
                    flagInvalidValue
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Responsible Driver
                </label>
                <TypeAhead
                  options={driverOptions}
                  value={sf.responsibledriver}
                  onChange={(next) => setSf((p) => ({ ...p, responsibledriver: next }))}
                  onClear={() => setSf((p) => ({ ...p, responsibledriver: '' }))}
                  placeholder="Search drivers…"
                  disabled={saving}
                  showSearch
                  showDescriptions
                  groupBy
                  flagInvalidValue
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Comments
                </label>
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={sf.comments}
                  onChange={(e) => setSf((p) => ({ ...p, comments: e.target.value }))}
                  disabled={saving}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-label bg-panel border border-gray-300 rounded hover:bg-highlight dark:border-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          {canBulkCreate && (
            <button
              type="button"
              onClick={openBulkFanout}
              disabled={saving || !!blockedReason}
              title={
                blockedReason
                  ?? `Create this ticket on every pro in the Change All set (${changeAll.targetPros.length})`
              }
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply to all {changeAll.targetPros.length} pros
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges || blockRescheduleForUnsavedChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isCreate ? 'Creating...' : 'Saving...'}
              </>
            ) : isCreate ? (
              'Create Ticket'
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>

      {canBulkCreate && rescheduleAsk && (() => {
        const copy = rescheduleFanoutPrompt({
          canClearDispatch,
          proCount: changeAll.targetPros.length,
        });
        // Unlike the single-pro dialog, both boxes are always offered: which
        // targets a pro actually has differs across the set and is not known
        // until the preview runs. Pros the answer does not fit are reported
        // there, per pro, before anything is applied.
        const box = (
          key: keyof RescheduleDateChoice,
          label: string,
          note: string,
        ) => (
          <label className="flex items-start gap-3 rounded border border-slate-200 dark:border-gray-700 px-3 py-2 cursor-pointer hover:bg-highlight">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={bulkChoice[key]}
              onChange={(e) =>
                setBulkChoice((c) => ({ ...c, [key]: e.target.checked }))}
            />
            <span>
              <span className="font-medium text-body">{label}</span>
              <span className="block text-xs text-muted">{note}</span>
            </span>
          </label>
        );
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-scrim/40">
            <div className="w-[34rem] max-w-[90vw] rounded-lg bg-panel shadow-xl">
              <div className="px-5 py-3 border-b border-slate-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-heading">{copy.title}</h3>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm text-label">
                <p className="text-xs text-muted">{copy.body}</p>
                {box('delivery', RESCHEDULE_DELIVERY_LABEL, copy.deliveryNote)}
                {box('prepull', RESCHEDULE_PREPULL_LABEL, copy.prepullNote)}
                <p className="text-xs text-muted">{copy.footnote}</p>
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => setRescheduleAsk(false)}
                  className="px-4 py-2 text-sm font-medium text-label bg-panel border border-gray-300 rounded hover:bg-highlight dark:border-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={answerReschedule}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {canBulkCreate && (
        <ChangeAllReviewModal
          isOpen={bulkOpen}
          pros={changeAll.targetPros}
          title="Change All — Trouble Ticket"
          summary={
            <span>
              Create this {currentCategory || 'trouble'} ticket on{' '}
              {changeAll.targetPros.length} pro(s)
              {bulkSf ? ', with its service failure' : ''}
              {isRescheduleRequested && (
                <>
                  {' — '}
                  {rescheduleFanoutSummary(bulkChoice, canClearDispatch)}
                </>
              )}
            </span>
          }
          previewFn={(pros, signal) =>
            // The answer rides along so the per-pro warnings are concrete: which
            // pros have no pre-pull to clear, and who is about to lose a driver's
            // arrival/departure stamps.
            troubleTicketsService.changeAllPreview(
              {
                pros,
                ticket: bulkTicket,
                removeScheduledDate: bulkChoice.delivery,
                removePrepullDate: bulkChoice.prepull,
              },
              signal,
            )
          }
          applyFn={(chunk, nonce) =>
            troubleTicketsService.changeAllApply({
              pros: chunk,
              ticket: bulkTicket,
              serviceFailure: bulkSf,
              submitNonce: nonce,
              removeScheduledDate: bulkChoice.delivery,
              removePrepullDate: bulkChoice.prepull,
            })
          }
          onClose={() => {
            setBulkOpen(false);
            // The fan-out already created this ticket on every pro in the set, the
            // anchor included — leaving the create form open would invite a duplicate
            // via "Create Ticket". (The notes arm clears its textarea for the same
            // reason; here the whole form is the input.)
            if (bulkApplied) onClose();
          }}
          onApplied={() => {
            setBulkApplied(true);
            // The anchor is in the target set, so its own panel needs the refresh too.
            onSaved();
            onServiceFailureSaved?.();
            // A reschedule fan-out cleared this pro's driver/appt dates server-side;
            // without this the drawer keeps showing the pre-clear leg values. Same
            // callback the single-pro path uses.
            if (isRescheduleRequested) void onRescheduleApplied?.();
          }}
        />
      )}

      {ticket && (
        <RecordHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          endpoint={`/trouble-tickets/${ticket.recno}/history`}
          title={`Trouble Ticket #${ticket.recno} History`}
          subtitle={effectivePro ? `PRO ${effectivePro}` : undefined}
          fieldLabels={TT_HISTORY_FIELD_LABELS}
        />
      )}
    </div>,
    document.body,
  );
};

export default TroubleTicketEditModal;
